import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";

const JsonDataModal = ({ open, onClose, data, title }) => {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const auditData = data?.data ?? data?.jsonData;

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formatJson(auditData));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatJson = (jsonString) => {
    if (typeof jsonString !== "string") {
      return JSON.stringify(jsonString, null, 2);
    }
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch (error) {
      return jsonString;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "400px",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {title || t("activityLog.jsonModal.title")}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {auditData ? (
            <Box
              component="pre"
              sx={{
                backgroundColor: "#f5f5f5",
                padding: 2,
                borderRadius: 1,
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "0.875rem",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {formatJson(auditData)}
            </Box>
          ) : (
            <Typography color="text.secondary">
              {t("activityLog.jsonModal.noData")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyToClipboard}
            disabled={!auditData}
          >
            {t("activityLog.jsonModal.copy")}
          </Button>
          <Button onClick={onClose} variant="contained">
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("activityLog.jsonModal.copied")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JsonDataModal;
